import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewChecked, OnDestroy, HostListener, SimpleChanges } from '@angular/core';
import { ChatDocumentCard, ChatMessage, DrtEditableField, DrtEditableSection } from '../models/chat-message.model';
import { DrtProgressStep } from '../services/chat.service';

export interface ChatFeedbackEvent {
    message: ChatMessage;
    messageIndex: number;
    selection: 'like' | 'dislike';
    relevance?: 'very' | 'partial' | 'not';
    completeness?: 'complete' | 'incomplete' | 'unsure';
    rating?: number;
    comments?: string;
}

export interface DrtGenerateEvent {
    message: ChatMessage;
    process_info: Record<string, string>;
    scope_parameters: Record<string, string>;
}

export interface EwoShowMoreEvent {
    message: ChatMessage;
}

interface EwoPreviewField {
    label: string;
    value: string;
}

interface EwoFilterStateEntry {
    orderedIds: string[];
    messageId?: string;
}

interface RelatedWorkOrderLink {
    id: string;
    title: string;
    url: string;
    description: string;
}

declare global {
    interface Window {
        MathJax: any;
    }
}

@Component({
    selector: 'app-chat-interface',
    templateUrl: './chat-interface.component.html',
    styleUrls: ['./chat-interface.component.scss'],
    standalone: false
})
export class ChatInterfaceComponent implements AfterViewChecked, OnDestroy {
    @Input() isChatMode: boolean = false;
    @Input() chatMessages: ChatMessage[] = [];
    @Input() welcomeText: string = '';
    @Input() isLoading: boolean = false;
    @Input() drtProgress: DrtProgressStep[] = [];
    @Input() animationComplete: boolean = false;
    @Input() suggestionsDisabled: boolean = false;

    @Output() feedbackSubmitted = new EventEmitter<ChatFeedbackEvent>();
    @Output() feedbackDialogStateChanged = new EventEmitter<boolean>();
    @Output() drtGenerateRequested = new EventEmitter<DrtGenerateEvent>();
    @Output() suggestionClicked = new EventEmitter<string>();
    @Output() ewoShowMoreRequested = new EventEmitter<EwoShowMoreEvent>();

    @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

    public isFeedbackDialogOpen: boolean = false;
    public previewDocument: ChatDocumentCard | null = null;
    public readonly generalFallbackReasoning =
        'CES Search Agent cited no sources, so General Knowledge Agent was invoked as a fallback.';
    public previewDrawerWidth: number = 520;
    public isEwoPreviewExpanded: boolean = false;
    public readonly maxDrtSuggestions: number = 12;
    public readonly initialDrtSuggestionsCount: number = 5;
    private pendingScrollTarget: 'bottom' | 'latest-bot-start' | null = null;
    private lastMessageCount: number = 0;
    private activeDrtFieldId: string | null = null;
    private showDefaultDrtOptionsFieldId: string | null = null;
    private drtBlurTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private isResizingPreviewDrawer: boolean = false;
    private resizeStartX: number = 0;
    private resizeStartWidth: number = 520;

    // EWO Smart Filter state: maps message index -> visible doc IDs scoped to message id.
    // Empty orderedIds means "no active state, show all in original order".
    private ewoFilterState = new Map<number, EwoFilterStateEntry>();
    private readonly expandableEwoFieldLabels = new Set([
        'permitapplicability',
        'permitapplicatibility',
        'dateofexecution',
        'codeapplicability',
        'codeapplicatbility',
        'safetynotes',
        'relatedcancelledworkorders',
        'workinstructions',
        'keywords'
    ]);

    public hasEwoCards(message: ChatMessage): boolean {
        return !!(message.documents && message.documents.some(d => d.documentType === 'EWO'));
    }

    public onEwoFilterChange(messageIndex: number, orderedIds: string[], messageId?: string): void {
        this.ewoFilterState.set(messageIndex, {
            orderedIds,
            messageId
        });
    }

    /**
     * Returns the display list for the document grid.
     * When sort/filter is active, returns filtered+sorted subset.
     * Otherwise returns the original full list.
     */
    public getDisplayDocs(messageIndex: number, message: ChatMessage): import('../models/chat-message.model').ChatDocumentCard[] {
        const docs = message.documents || [];
        const state = this.ewoFilterState.get(messageIndex);
        if (state?.messageId && message.id && state.messageId !== message.id) {
            return docs;
        }
        // Empty array or no state = default: show all in original order
        if (!state || state.orderedIds.length === 0) return docs;

        // Reorder docs according to the ordered ID list emitted by filter bar
        return state.orderedIds
            .map(id => docs.find(d => d.id === id))
            .filter((d): d is import('../models/chat-message.model').ChatDocumentCard => !!d);
    }

    public isDocVisible(messageIndex: number, doc: import('../models/chat-message.model').ChatDocumentCard): boolean {
        const state = this.ewoFilterState.get(messageIndex);
        if (state?.messageId && doc.id && this.chatMessages[messageIndex]?.id && state.messageId !== this.chatMessages[messageIndex].id) {
            return true;
        }
        if (!state || state.orderedIds.length === 0) return true;
        return state.orderedIds.includes(doc.id);
    }

    public getVisibleDocCount(messageIndex: number, message: ChatMessage): number {
        const docs = message.documents || [];
        const state = this.ewoFilterState.get(messageIndex);
        if (state?.messageId && message.id && state.messageId !== message.id) return docs.length;
        if (!state || state.orderedIds.length === 0) return docs.length;
        return docs.filter(d => state.orderedIds.includes(d.id)).length;
    }

    public getSecondaryAgentsDisplay(secondaryAgents?: string[]): string {
        if (!secondaryAgents || secondaryAgents.length === 0) {
            return '';
        }

        return secondaryAgents
            .map(agent => this.getAgentDisplay(agent))
            .filter(Boolean)
            .join(', ');
    }

    public getAgentDisplay(agent?: string): string {
        const normalizedAgent = (agent || '').trim();
        if (!normalizedAgent) {
            return '';
        }

        const knownAgents: Record<string, string> = {
            ces_search: 'CES Search Agent',
            ces_lite: 'CES Lite Agent',
            general: 'General Knowledge Agent',
            ewo: 'EWO Agent',
            ewo_qna: 'EWO Q&A Agent',
            drt: 'DRT Agent',
            drt_qna: 'DRT Q&A Agent',
            esra: 'ESRA Agent',
            legacy_piping: 'Legacy Piping Agent',
            rims: 'RIMS Agent',
            out_of_scope: 'Out of Scope'
        };

        const lookupKey = normalizedAgent.toLowerCase();
        const legacyAgentAliases: Record<string, string> = {
            general_engineering: 'general',
            'general engineering agent': 'general',
            'ewo search agent': 'ewo',
            'esra search agent': 'esra',
            'esra orchestrator': 'esra',
            'esra welding agent': 'esra'
        };
        const canonicalKey = legacyAgentAliases[lookupKey] || lookupKey;
        if (knownAgents[canonicalKey]) {
            return knownAgents[canonicalKey];
        }

        if (/agent$/i.test(normalizedAgent)) {
            return normalizedAgent;
        }

        return normalizedAgent
            .split(/[_\s-]+/)
            .filter(Boolean)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ');
    }

    ngAfterViewChecked() {
        if (this.pendingScrollTarget) {
            if (this.pendingScrollTarget === 'latest-bot-start') {
                this.scrollToLatestBotMessageStart();
            } else {
                this.scrollToBottom();
            }

            this.pendingScrollTarget = null;
        }

        // Trigger MathJax rendering when new messages appear
        if (this.chatMessages.length > this.lastMessageCount) {
            this.lastMessageCount = this.chatMessages.length;
            this.renderMathJax();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        const chatMessagesChange = changes['chatMessages'];
        if (chatMessagesChange) {
            const previousMessages = (chatMessagesChange.previousValue as ChatMessage[] | undefined) ?? [];
            const currentMessages = (chatMessagesChange.currentValue as ChatMessage[] | undefined) ?? [];

            if (currentMessages.length > previousMessages.length) {
                const lastMessage = currentMessages[currentMessages.length - 1];
                this.pendingScrollTarget = lastMessage?.sender === 'bot'
                    ? 'latest-bot-start'
                    : 'bottom';
            }
        }

        const isLoadingChange = changes['isLoading'];
        if (isLoadingChange?.currentValue && !isLoadingChange.previousValue && !this.pendingScrollTarget) {
            this.pendingScrollTarget = 'bottom';
        }
    }

    ngOnDestroy() {
        this.updatePreviewLayoutClass(false);
    }

    /**
     * Trigger MathJax to re-render LaTeX formulas
     */
    private renderMathJax(): void {
        setTimeout(() => {
            if (window.MathJax && window.MathJax.typesetPromise) {
                try {
                    window.MathJax.typesetPromise().catch(() => {
                        // Silently handle if MathJax hasn't fully loaded yet
                    });
                } catch (e) {
                    // MathJax not ready, will retry on next view check
                }
            }
        }, 100);
    }

    private scrollToBottom(): void {
        try {
            if (this.messagesContainer) {
                this.messagesContainer.nativeElement.scrollTop =
                    this.messagesContainer.nativeElement.scrollHeight;
            }
        } catch (err) {
            console.error('Scroll error:', err);
        }
    }

    private scrollToLatestBotMessageStart(): void {
        try {
            if (!this.messagesContainer) {
                return;
            }

            const container = this.messagesContainer.nativeElement as HTMLElement;
            const botMessages = container.querySelectorAll('[data-message-role="bot"]');
            const latestBotMessage = botMessages[botMessages.length - 1] as HTMLElement | undefined;

            if (!latestBotMessage) {
                this.scrollToBottom();
                return;
            }

            const containerRect = container.getBoundingClientRect();
            const messageRect = latestBotMessage.getBoundingClientRect();
            const offset = messageRect.top - containerRect.top;

            container.scrollTop += offset - 8;
        } catch (err) {
            console.error('Scroll error:', err);
        }
    }

    public onFeedbackAction(message: ChatMessage, selection: 'like' | 'dislike'): void {
        if (!this.canShowFeedbackControls(message)) {
            return;
        }

        if (message.feedbackSubmitted) {
            return;
        }

        if (message.feedbackDialogOpen && message.feedbackSelection === selection) {
            message.feedbackDialogOpen = false;
            this.updateFeedbackDialogState();
            return;
        }

        message.feedbackSelection = selection;
        message.feedbackDialogOpen = true;
        this.updateFeedbackDialogState();
    }

    public canShowFeedbackControls(message: ChatMessage): boolean {
        return message.sender === 'bot'
            && message.type !== 'drt-parameters'
            && !message.conversationPendingSave;
    }

    public isFeedbackSubmitDisabled(message: ChatMessage): boolean {
        if (!message.feedbackRelevance || !message.feedbackCompleteness) {
            return true;
        }
        if (message.feedbackSelection === 'dislike') {
            const comment = (message.feedbackComments || '').trim();
            if (!comment) {
                return true;
            }
        }
        return false;
    }

    public getDrtSections(message: ChatMessage): DrtEditableSection[] {
        return message.drtParameters?.sections || [];
    }

    public trackByDrtField(_index: number, field: { key: string }): string {
        return field.key;
    }

    /** Equipment-only fields; hidden while the work type is piping and they are blank. */
    private readonly equipmentOnlyDrtKeys = new Set(['equipment_tag_number', 'vessel_new_or_old', 'equipment_thickness_inches']);

    private isHiddenEquipmentField(section: DrtEditableSection, field: DrtEditableField): boolean {
        if (section.id !== 'scope_parameters' || !this.equipmentOnlyDrtKeys.has(field.key)) {
            return false;
        }
        const workType = section.fields.find(f => f.key === 'piping_or_equipment')?.value;
        return String(workType ?? '').trim().toLowerCase() === 'piping' && !String(field.value ?? '').trim();
    }

    /** Fields to render for a section: all of them once expanded, else only the "important" ones. */
    public getVisibleDrtFields(section: DrtEditableSection): DrtEditableField[] {
        const fields = section.fields.filter(f => !this.isHiddenEquipmentField(section, f));
        if (section.expanded || section.id !== 'scope_parameters') {
            return fields;
        }
        return fields.filter(f => f.important);
    }

    /** True when the visible field at `index` starts a new group (only grouped fields get a heading). */
    public showDrtGroupHeading(section: DrtEditableSection, index: number): boolean {
        const fields = this.getVisibleDrtFields(section);
        const group = fields[index]?.group;
        return !!group && (index === 0 || fields[index - 1].group !== group);
    }

    /** Visible fields bucketed by consecutive group; ungrouped fields share one unnamed bucket. */
    public getVisibleDrtGroups(section: DrtEditableSection): { name: string; fields: DrtEditableField[] }[] {
        const groups: { name: string; fields: DrtEditableField[] }[] = [];
        for (const field of this.getVisibleDrtFields(section)) {
            const name = field.group || '';
            const last = groups[groups.length - 1];
            if (last && last.name === name) {
                last.fields.push(field);
            } else {
                groups.push({ name, fields: [field] });
            }
        }
        return groups;
    }

    public trackByDrtGroup(index: number, group: { name: string }): string {
        return group.name || `__ungrouped_${index}`;
    }

    public getHiddenDrtFieldCount(section: DrtEditableSection): number {
        if (section.id !== 'scope_parameters') {
            return 0;
        }
        return section.fields.filter(f => !f.important && !this.isHiddenEquipmentField(section, f)).length;
    }

    public toggleDrtSectionExpanded(section: DrtEditableSection): void {
        section.expanded = !section.expanded;
    }

    public hasDrtDropdownOptions(_message: ChatMessage, field: DrtEditableField): boolean {
        return (field.dropdownOptions || []).length > 0;
    }

    public onDrtFieldFocus(message: ChatMessage, section: DrtEditableSection, field: DrtEditableField): void {
        this.clearDrtBlurTimeout();
        this.activeDrtFieldId = this.getDrtFieldId(message, section, field);
    }

    public onDrtFieldInput(message: ChatMessage, section: DrtEditableSection, field: DrtEditableField): void {
        if (!this.hasDrtDropdownOptions(message, field)) {
            return;
        }

        this.activeDrtFieldId = this.getDrtFieldId(message, section, field);
        this.showDefaultDrtOptionsFieldId = null;
    }

    public onDrtFieldBlur(message?: ChatMessage, section?: DrtEditableSection, field?: DrtEditableField): void {
        this.clearDrtBlurTimeout();
        this.drtBlurTimeoutId = setTimeout(() => {
            this.activeDrtFieldId = null;
            this.showDefaultDrtOptionsFieldId = null;
            if (message && field && !this.validateDrtField(message, field)) {
                // Strict field: an unlisted value is not accepted, so fall back to the last accepted one.
                field.value = field.committedValue ?? '';
            }
        }, 120);
    }

    /** Whether only a listed option is accepted for this field right now. */
    private isDrtFieldStrict(message: ChatMessage, field: DrtEditableField): boolean {
        if (!(field.dropdownOptions || []).length) {
            return false;
        }
        return !!field.strict;
    }

    /** Validates one field; strict fields must hold a listed option (a unique prefix is completed). Blank is allowed. */
    private validateDrtField(message: ChatMessage, field: DrtEditableField): boolean {
        field.error = undefined;
        const value = (field.value ?? '').trim();
        if (!value || !this.isDrtFieldStrict(message, field)) {
            field.committedValue = field.value;
            return true;
        }
        const options = field.dropdownOptions || [];
        const lower = value.toLowerCase();
        const exact = options.find(option => option.toLowerCase() === lower);
        const candidates = exact ? [exact] : options.filter(option => option.toLowerCase().startsWith(lower));
        if (candidates.length === 1) {
            field.value = candidates[0];
            field.committedValue = candidates[0];
            return true;
        }
        field.error = `"${value}" is not an allowed option. Choose a value from the list.`;
        return false;
    }

    public toggleDrtFieldSuggestions(
        message: ChatMessage,
        section: DrtEditableSection,
        field: DrtEditableField,
        event: MouseEvent
    ): void {
        event.preventDefault();
        event.stopPropagation();

        if (!this.hasDrtDropdownOptions(message, field)) {
            return;
        }

        this.clearDrtBlurTimeout();
        const fieldId = this.getDrtFieldId(message, section, field);

        if (this.activeDrtFieldId === fieldId) {
            this.activeDrtFieldId = null;
            this.showDefaultDrtOptionsFieldId = null;
            return;
        }

        this.activeDrtFieldId = fieldId;
        this.showDefaultDrtOptionsFieldId = fieldId;
    }

    public shouldShowDrtSuggestions(message: ChatMessage, section: DrtEditableSection, field: DrtEditableField): boolean {
        if (!this.hasDrtDropdownOptions(message, field)) {
            return false;
        }

        const fieldId = this.getDrtFieldId(message, section, field);
        if (this.activeDrtFieldId !== fieldId) {
            return false;
        }

        return this.getFilteredDrtOptions(message, section, field).length > 0;
    }

    public shouldShowDrtNoMatchState(message: ChatMessage, section: DrtEditableSection, field: DrtEditableField): boolean {
        if (!this.hasDrtDropdownOptions(message, field)) {
            return false;
        }

        const fieldId = this.getDrtFieldId(message, section, field);
        if (this.activeDrtFieldId !== fieldId) {
            return false;
        }

        const query = (field.value || '').trim();
        if (!query) {
            return false;
        }

        return this.getFilteredDrtOptions(message, section, field).length === 0;
    }

    public getFilteredDrtOptions(_message: ChatMessage, section: DrtEditableSection, field: DrtEditableField): string[] {
        const options = field.dropdownOptions || [];
        const query = (field.value || '').trim().toLowerCase();
        const fieldId = this.getDrtFieldId(_message, section, field);
        const openedByArrow = this.showDefaultDrtOptionsFieldId === fieldId;

        // Arrow click lists every option regardless of the current value; Pipe Spec is too long, so it keeps prefix matching.
        if (openedByArrow && !this.isDrtPipeSpecField(field)) {
            return options;
        }

        if (!query) {
            return openedByArrow ? options.slice(0, this.initialDrtSuggestionsCount) : [];
        }

        return options.filter(option => option.toLowerCase().startsWith(query));
    }

    private isDrtPipeSpecField(field: DrtEditableField): boolean {
        return field.key === 'Pipe Spec' || field.key === 'pipe_spec_number';
    }

    public selectDrtOption(
        message: ChatMessage,
        section: DrtEditableSection,
        field: DrtEditableField,
        option: string,
        event: MouseEvent
    ): void {
        event.preventDefault();
        event.stopPropagation();

        field.value = option;
        field.committedValue = option;
        field.error = undefined;
        this.clearDrtBlurTimeout();
        this.activeDrtFieldId = null;
        this.showDefaultDrtOptionsFieldId = null;
    }

    public onDrtGenerate(message: ChatMessage): void {
        console.info('[DRT] Confirm and proceed button clicked', {
            threadId: message.threadId,
            hasRawData: !!message.drtRawData,
            hasSourcePdf: !!message.drtSourcePdfFileB64,
        });

        const sections = this.getDrtSections(message);

        // Strict fields must hold a listed option before generation; the first problem blocks it.
        const invalidFields = sections
            .flatMap(section => section.fields)
            .filter(field => !this.validateDrtField(message, field));
        if (invalidFields.length > 0) {
            console.warn('[DRT] Blocked generate: fields outside their allowed options', invalidFields.map(f => f.key));
            // Make sure an invalid field tucked behind "See more" is visible.
            sections.filter(s => s.fields.some(f => f.error)).forEach(s => { s.expanded = true; });
            return;
        }

        // scope_review section is the new review card; treat its fields as process_info
        const processSection = sections.find(section => section.id === 'process_info' || section.id === 'scope_review');
        const scopeSection = sections.find(section => section.id === 'scope_parameters');

        const process_info: Record<string, string> = {};
        const scope_parameters: Record<string, string> = {};

        (processSection?.fields || []).forEach(field => {
            process_info[field.key] = (field.value ?? '').toString();
        });

        (scopeSection?.fields || []).forEach(field => {
            scope_parameters[field.key] = (field.value ?? '').toString();
        });

        message.drtGeneratePending = true;
        this.drtGenerateRequested.emit({
            message,
            process_info,
            scope_parameters
        });
    }

    public closeFeedback(message: ChatMessage): void {
        message.feedbackDialogOpen = false;
        message.feedbackSelection = undefined;
        this.updateFeedbackDialogState();
    }

    private updateFeedbackDialogState(): void {
        const anyFeedbackOpen = this.chatMessages.some(msg => msg.feedbackDialogOpen);
        this.isFeedbackDialogOpen = anyFeedbackOpen;
        this.feedbackDialogStateChanged.emit(anyFeedbackOpen);
    }

    public submitFeedback(message: ChatMessage, messageIndex: number): void {
        if (!this.canShowFeedbackControls(message)) {
            return;
        }

        if (!message.feedbackSelection) {
            return;
        }

        message.feedbackSubmitted = true;
        message.feedbackDialogOpen = false;

        this.feedbackSubmitted.emit({
            message,
            messageIndex,
            selection: message.feedbackSelection,
            relevance: message.feedbackRelevance,
            completeness: message.feedbackCompleteness,
            rating: message.feedbackRating,
            comments: message.feedbackComments
        });
        this.updateFeedbackDialogState();
    }

    public isMarkdownMessage(message: ChatMessage): boolean {
        return message.isMarkdown === true;
    }

    private hasSourcesReferencedInResponse(text: string): boolean {
        if (!text || !text.trim()) {
            return false;
        }

        return /(^|\n)\s{0,3}(?:#{1,6}\s*)?sources?\s+referenced\b/i.test(text);
    }

    public onPreviewDocument(document: ChatDocumentCard): void {
        this.previewDocument = document;
        this.isEwoPreviewExpanded = false;
        (this.previewDocument as any).__cancelledWorkOrders = this.getRelatedWorkOrdersByKeyword(document, /cancel+ed?/i);
        (this.previewDocument as any).__revisionWorkOrders = this.getRelatedWorkOrdersByKeyword(document, /revision/i);
        this.updatePreviewLayoutClass(true);
    }

    public closePreviewDocument(): void {
        this.previewDocument = null;
        this.isEwoPreviewExpanded = false;
        this.isResizingPreviewDrawer = false;
        this.updatePreviewLayoutClass(false);
    }

    public toggleEwoPreviewExpanded(): void {
        this.isEwoPreviewExpanded = !this.isEwoPreviewExpanded;
    }

    public startPreviewResize(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.isResizingPreviewDrawer = true;
        this.resizeStartX = event.clientX;
        this.resizeStartWidth = this.previewDrawerWidth;
    }

    @HostListener('document:mousemove', ['$event'])
    public onPreviewResize(event: MouseEvent): void {
        if (!this.isResizingPreviewDrawer) {
            return;
        }

        const deltaX = this.resizeStartX - event.clientX;
        const nextWidth = this.resizeStartWidth + deltaX;
        this.previewDrawerWidth = this.clampPreviewDrawerWidth(nextWidth);
    }

    @HostListener('document:mouseup')
    public stopPreviewResize(): void {
        this.isResizingPreviewDrawer = false;
    }

    @HostListener('document:keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape' && this.previewDocument) {
            this.closePreviewDocument();
        }
    }

    private getDrtFieldId(message: ChatMessage, section: DrtEditableSection, field: DrtEditableField): string {
        const messageKey = message.id || message.threadId || 'drt-message';
        return `${messageKey}::${section.id}::${field.key}`;
    }

    private clearDrtBlurTimeout(): void {
        if (this.drtBlurTimeoutId) {
            clearTimeout(this.drtBlurTimeoutId);
            this.drtBlurTimeoutId = null;
        }
    }

    public onDocumentSelectionChange(doc: any, event: Event): void {
        const input = event.target as HTMLInputElement;
        doc.selected = input.checked;
    }

    public canShowEwoShowMore(message: ChatMessage): boolean {
        if (!this.hasEwoCards(message) || message.conversationPendingSave) {
            return false;
        }

        const primaryAgent = (message.routing?.primary_agent || message.agent || '').toLowerCase();
        return primaryAgent.includes('ewo');
    }

    public onShowMoreEwos(message: ChatMessage): void {
        this.ewoShowMoreRequested.emit({ message });
    }

    private updatePreviewLayoutClass(isOpen: boolean): void {
        const appRoot = document.querySelector('app-root');
        if (!appRoot) {
            return;
        }

        if (isOpen) {
            appRoot.classList.add('document-preview-open');
            return;
        }

        appRoot.classList.remove('document-preview-open');
    }

    private clampPreviewDrawerWidth(width: number): number {
        const minWidth = 420;
        const maxWidth = Math.max(560, Math.floor(window.innerWidth * 0.85));
        return Math.min(Math.max(width, minWidth), maxWidth);
    }

    public getEwoPreviewFields(document: ChatDocumentCard | null): EwoPreviewField[] {
        if (!document || document.documentType !== 'EWO') {
            return [];
        }

        const markdown = (document.previewContent || document.fullContent || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        if (!markdown.trim()) {
            return this.getFallbackEwoFields(document);
        }

        const fields: EwoPreviewField[] = [];
        const lines = markdown.split('\n');
        let currentField: EwoPreviewField | null = null;

        for (const rawLine of lines) {
            const line = rawLine.trim();

            if (!line || this.isPreviewStructureLine(line)) {
                if (this.startsNewPreviewSection(line)) {
                    currentField = null;
                }
                continue;
            }

            const fieldMatch = line.match(/^\*\*(.+?):\*\*\s*(.*)$/);
            if (fieldMatch) {
                const label = fieldMatch[1].trim();
                // Skip the "Filters Applied" header — it belongs above the card grid, not in each card's preview
                if (label === 'Filters Applied') {
                    currentField = null;
                    continue;
                }
                const value = this.cleanPreviewFieldValue(fieldMatch[2].trim());
                currentField = {
                    label,
                    value
                };
                fields.push(currentField);
                continue;
            }

            if (currentField) {
                const continuationValue = this.cleanPreviewFieldValue(line);
                if (!continuationValue) {
                    currentField = null;
                    continue;
                }

                currentField.value = `${currentField.value} ${continuationValue}`.trim();
            }
        }

        if (fields.length > 0) {
            return fields.filter(field => field.label.toLowerCase() !== 'drt parameters');
        }

        return this.getFallbackEwoFields(document);
    }

    public getDefaultEwoPreviewFields(document: ChatDocumentCard | null): EwoPreviewField[] {
        return this.getEwoPreviewFields(document).filter(field => !this.isExpandableEwoField(field.label));
    }

    public getExpandedEwoPreviewFields(document: ChatDocumentCard | null): EwoPreviewField[] {
        return this.getEwoPreviewFields(document).filter(field => this.isExpandableEwoField(field.label));
    }

    public hasExpandableEwoPreviewContent(document: ChatDocumentCard | null): boolean {
        if (!document || document.documentType !== 'EWO') {
            return false;
        }

        if (this.getExpandedEwoPreviewFields(document).length > 0) {
            return true;
        }

        return (((document as any)?.__cancelledWorkOrders || []) as RelatedWorkOrderLink[]).length > 0;
    }

    public getEwoPreviewContent(document: ChatDocumentCard | null): string {
        if (!document || document.documentType !== 'EWO') {
            return '';
        }

        return (document.previewContent || document.fullContent || '').trim();
    }

    private getRelatedWorkOrdersByKeyword(document: ChatDocumentCard | null, keywordPattern: RegExp): RelatedWorkOrderLink[] {
        const markdown = this.getEwoPreviewContent(document);
        if (!markdown) {
            return [];
        }

        const normalized = markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        const blocks = normalized.split(/\n[ \t]*---[ \t]*\n/);
        const matchedBlocks = blocks.filter(block => keywordPattern.test(block));

        if (!matchedBlocks.length) {
            return [];
        }

        const links: RelatedWorkOrderLink[] = [];
        const seen = new Set<string>();
        const linkPattern = /^\s*[-*]\s*(?:[^\[]*?)\[([^\]]+)\]\((https?:\/\/[^)]+)\)\s*(?:[\u2014-]\s*(.+))?\s*$/gm;

        for (const block of matchedBlocks) {
            let match: RegExpExecArray | null = null;

            while ((match = linkPattern.exec(block)) !== null) {
                const id = (match[1] || '').trim();
                const url = (match[2] || '').trim();
                const description = (match[3] || '').trim();
                const dedupeKey = `${id}|${url}`;

                if (!id || !url || seen.has(dedupeKey)) {
                    continue;
                }

                seen.add(dedupeKey);
                links.push({
                    id,
                    title: id,
                    url,
                    description
                });
            }
        }

        return links;
    }

    private getFallbackEwoFields(document: ChatDocumentCard): EwoPreviewField[] {
        const mapped: EwoPreviewField[] = [
            { label: 'EWO ID', value: document.ewoId || '' },
            { label: 'Title', value: document.title || '' },
            { label: 'Abstract', value: document.abstract || document.summary || '' },
            { label: 'Facility/Business Unit', value: document.facility || '' },
            { label: 'Discipline', value: document.discipline || '' },
            { label: 'Category', value: document.category || '' },
            { label: 'Sub-Category', value: document.subCategory || '' },
            { label: 'Equipment Type', value: document.equipmentType || '' },
            { label: 'Equipment Tags', value: document.equipmentTags || '' },
            { label: 'Process Medium', value: document.processMedium || '' },
            { label: 'Date of Execution', value: document.date || '' },
            { label: 'Scope of Work', value: document.scopeOfWork || '' },
            { label: 'Key Learnings', value: document.keyLearnings || '' }
        ];

        return mapped
            .filter(field => field.value.trim().length > 0)
            .filter(field => field.label.toLowerCase() !== 'drt parameters');
    }

    private isExpandableEwoField(label: string): boolean {
        return this.expandableEwoFieldLabels.has(this.normalizePreviewFieldLabel(label));
    }

    private normalizePreviewFieldLabel(label: string): string {
        return (label || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    private isPreviewStructureLine(line: string): boolean {
        return line === '---'
            || line.startsWith('### ')
            || /^<\/?details/i.test(line)
            || /^<\/?summary/i.test(line)
            || /^[-*]\s+\[/.test(line);
    }

    private startsNewPreviewSection(line: string): boolean {
        return line === '---'
            || line.startsWith('### ')
            || /^<\/?details/i.test(line)
            || /^<\/?summary/i.test(line);
    }

    private cleanPreviewFieldValue(value: string): string {
        return (value || '')
            .replace(/<details[\s\S]*$/i, '')
            .replace(/<summary[\s\S]*$/i, '')
            .replace(/---[\s\S]*$/i, '')
            .trim();
    }
}
